import { FC, useMemo, useState, useRef, useLayoutEffect } from 'react'
import cn from 'classnames'

import Badge from '../../badge/badge'
import Button from '../../button/button'
import DropdownButton from '../../dropdown-button/dropdown-button'
import { IListItemProps } from '../../list'

import { formatDateString } from '../../../util'

import { ISafetyCase } from '../../../interfaces'

import s from '../card.module.scss'
import { baseStatusConst } from '../../../util/options/base-status'

type SafetyCaseCardProps = ISafetyCase & IListItemProps<ISafetyCase>
type HazardRiskSummary = {
  label: string
  color: string
  count: number
  severitySortOrder: number
}

// Utility function to calculate how many items fit in a row given the row width and the widths of the items. It also takes an optional initialUsedWidth parameter to account for any additional space used by other elements in the row (e.g., padding, margin, overflow button).
const getFitCount = (itemRefs: (HTMLDivElement | null)[], rowWidth: number, initialUsedWidth = 0): number => {
  let usedWidth = initialUsedWidth
  let fitCount = 0

  for (let i = 0; i < itemRefs.length; i++) {
    const item = itemRefs[i]

    if (!item) {
      continue
    }

    usedWidth += item.offsetWidth
    // Add 60px buffer to account for padding/margin/overflow button
    if (usedWidth < rowWidth - 60) {
      fitCount += 1
    } else {
      break
    }
  }

  return fitCount
}

const SafetyCaseCard: FC<SafetyCaseCardProps> = props => {
  const {
    id,
    name,
    createdAt,
    lastUpdatedAt,
    lastUpdatedByName,
    parentStatus,
    selectedRiskMatrixCell,
    tags,
    hazards,
    componentRef,
    activeId,
    actionsButtonOptions,
    onTitleClick,
    onLocationClick,
  } = props

  const title = useMemo(() => `#${id} - ${name || 'New External Safety Report'}`, [id, name])

  const hazardsWithGeoJson = useMemo(() => (hazards || []).filter(hazard => Boolean(hazard.geoJson)), [hazards])

  // Refs for measuring the widths of the hazard, status, and tag badges to determine how many can fit in the visible row.
  const hazardsRowRef = useRef<HTMLDivElement>(null)
  const footerRowRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  const locationRef = useRef<HTMLButtonElement>(null)
  const hazardsLabelRef = useRef<HTMLSpanElement>(null)
  const hazardSummaryMeasureRefs = useRef<(HTMLDivElement | null)[]>([])
  const tagBadgeRefs = useRef<(HTMLDivElement | null)[]>([])

  // State to track how many tags and hazards fit in the visible row, and whether the tag overflow dropdown is open.
  const [tagFitCount, setTagFitCount] = useState<number>(tags ? tags.length : 0)
  const [hazardFitCount, setHazardFitCount] = useState<number>(hazards ? hazards.length : 0)
  const [showTagDropdown, setShowTagDropdown] = useState<boolean>(false)

  const hazardsWithSelectedRiskMatrixCell = useMemo(
    () => (hazards || []).filter(hazard => Boolean(hazard.selectedRiskMatrixCell)),
    [hazards],
  )

  const hazardRiskSummaries = useMemo(() => {
    const summaryMap = new Map<string, HazardRiskSummary>()

    hazardsWithSelectedRiskMatrixCell.forEach(hazard => {
      const label = hazard.selectedRiskMatrixCell?.riskLabel || 'Unknown'
      const color = hazard.selectedRiskMatrixCell?.riskColor || '#6c757d'
      const severitySortOrder = hazard.selectedRiskMatrixCell?.severitySortOrder || 0
      const key = `${label}|${id}`

      if (!summaryMap.has(key)) {
        summaryMap.set(key, { label, color, count: 0, severitySortOrder })
      }

      const current = summaryMap.get(key)
      if (current) {
        current.count += 1
        current.severitySortOrder = Math.max(current.severitySortOrder, severitySortOrder)
      }
    })

    return Array.from(summaryMap.values()).sort((a, b) => b.severitySortOrder - a.severitySortOrder)
  }, [hazardsWithSelectedRiskMatrixCell, id])

  const selectedRiskLabel = selectedRiskMatrixCell?.riskLabel?.trim()
  const showHazardRiskCount = hazardsWithSelectedRiskMatrixCell.length > 1
  const totalHazardsCount = hazards?.length || 0
  const showCompactHazardsSummary = hazardRiskSummaries.length > 0 && hazardFitCount < hazardRiskSummaries.length

  useLayoutEffect(() => {
    function recalc() {
      if (!hazardsRowRef.current || hazardRiskSummaries.length === 0) {
        setHazardFitCount(0)
      } else {
        const hazardsLabelWidth = hazardsLabelRef.current?.offsetWidth || 0
        const hazardFit = getFitCount(
          hazardSummaryMeasureRefs.current,
          hazardsRowRef.current.offsetWidth,
          hazardsLabelWidth,
        )

        setHazardFitCount(hazardFit)
      }

      if (!footerRowRef.current || !tags || tags.length === 0) {
        setTagFitCount(0)
      } else {
        const statusWidth = statusRef.current?.offsetWidth || 0
        const locationWidth = locationRef.current?.offsetWidth || 0
        const tagFit = getFitCount(tagBadgeRefs.current, footerRowRef.current.offsetWidth, statusWidth + locationWidth)

        setTagFitCount(tagFit)
      }
    }

    recalc()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recalc)

      return () => {
        window.removeEventListener('resize', recalc)
      }
    }

    const resizeObserver = new ResizeObserver(() => recalc())

    if (hazardsRowRef.current) {
      resizeObserver.observe(hazardsRowRef.current)
    }

    if (footerRowRef.current) {
      resizeObserver.observe(footerRowRef.current)
    }

    if (statusRef.current) {
      resizeObserver.observe(statusRef.current)
    }

    if (locationRef.current) {
      resizeObserver.observe(locationRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [tags, hazardRiskSummaries, showTagDropdown, hazardsWithGeoJson.length])

  const { visibleTags, overflowTags } = useMemo(() => {
    if (!tags) return { visibleTags: [], overflowTags: [] }

    return {
      visibleTags: tags.slice(0, tagFitCount),
      overflowTags: tags.slice(tagFitCount),
    }
  }, [tags, tagFitCount])

  const visibleHazardSummaries = useMemo(
    () => hazardRiskSummaries.slice(0, hazardFitCount),
    [hazardRiskSummaries, hazardFitCount],
  )

  const info = useMemo(() => {
    const formattedLastUpdatedAt = formatDateString(lastUpdatedAt)
    const formattedCreatedAt = formatDateString(createdAt)

    if (formattedLastUpdatedAt && lastUpdatedByName) {
      return `Updated at ${formattedLastUpdatedAt} by ${lastUpdatedByName}`
    }
    if (formattedCreatedAt) {
      return `Created at ${formattedCreatedAt}`
    }
    return ''
  }, [lastUpdatedAt, lastUpdatedByName, createdAt])

  return (
    <div
      className={cn(
        'card card-custom p-4',
        activeId === id && s['active-card'],
        parentStatus.baseStatus === baseStatusConst.TO_DO && s['highlight-card'],
        parentStatus.baseStatus === baseStatusConst.TO_DO ? s['highlight-card-border'] : 'border border-2',
      )}
      ref={componentRef}
    >
      <div className={cn(s['card-header'], 'card-header border-0 m-0 p-0')}>
        <div
          className={cn('card-title font-weight-bold flex-grow-1 mr-2', onTitleClick && 'cursor-pointer')}
          style={{ minWidth: 0 }}
          // @ts-ignore
          onClick={() => onTitleClick(props)}
        >
          <span className="d-block text-truncate">{title}</span>
        </div>

        {actionsButtonOptions && (
          <DropdownButton className={s['actions-button']} accessor={props} options={actionsButtonOptions} />
        )}
      </div>

      <div className="card-body p-0">{info && <div className="mt-2 text-truncate text-dark">{info}</div>}</div>

      <div className="border-0 card-footer d-flex flex-column mt-2 mx-0 p-0">
        <div ref={hazardsRowRef} className="d-flex align-items-center w-100">
          {hazardRiskSummaries.length > 0 ? (
            <>
              {showCompactHazardsSummary ? (
                <span ref={hazardsLabelRef} className="d-inline-block font-size-sm text-nowrap">
                  {totalHazardsCount} {totalHazardsCount === 1 ? 'Hazard' : 'Hazards'}
                </span>
              ) : (
                <>
                  <span ref={hazardsLabelRef} className="d-inline-block font-size-sm">
                    {totalHazardsCount} {totalHazardsCount === 1 ? 'Hazard' : 'Hazards'}:
                  </span>
                  {visibleHazardSummaries.map((summary, idx) => (
                    <div key={`${summary.label}-${idx}`} className="d-inline-flex align-items-center ml-2">
                      <span className={s['risk-dot']} style={{ backgroundColor: summary.color }} />
                      <span className="ml-1 font-size-sm text-nowrap">
                        {showHazardRiskCount ? `${summary.count} ` : ''}
                        {summary.label}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </>
          ) : selectedRiskLabel ? (
            <div className="d-inline-flex align-items-center ml-2">
              <span className={s['risk-dot']} style={{ backgroundColor: selectedRiskMatrixCell?.riskColor }} />
              <span className="ml-1 font-size-sm text-nowrap">{selectedRiskLabel}</span>
            </div>
          ) : null}
        </div>

        <div ref={footerRowRef} className="d-flex align-items-center justify-content-end w-100 mt-1">
          <div ref={statusRef} className="d-inline-block">
            <Badge
              color={parentStatus?.name === 'New' ? undefined : parentStatus?.baseColor}
              status={parentStatus?.name}
            />
          </div>

          {visibleTags.map(tag => (
            <div key={tag.name} className="d-inline-block ml-2">
              <Badge color="#902bf5" status={tag.name} />
            </div>
          ))}

          {overflowTags.length > 0 && (
            <div className="position-relative d-inline-block">
              <button
                type="button"
                className={`btn btn-sm ml-2 ${s['tag-overflow-button']}`}
                onClick={() => setShowTagDropdown((v: boolean) => !v)}
                aria-label={`Show ${overflowTags.length} more tags`}
              >
                +{overflowTags.length}
                {visibleTags.length === 0 ? ' tags' : ''}
              </button>
              {showTagDropdown && (
                <div className={s['tag-overflow-dropdown']}>
                  {overflowTags.map(tag => (
                    <Badge key={tag.name} color="#902bf5" status={tag.name} className="mb-2" />
                  ))}
                </div>
              )}
            </div>
          )}

          {hazardsWithGeoJson.length > 0 && (
            <Button
              componentRef={locationRef}
              type="ghost-icon"
              className="ml-2"
              onClick={() => onLocationClick?.(props)}
            >
              <i className="bi bi-geo-alt-fill text-primary" />
            </Button>
          )}
        </div>

        {/* Hidden measurement row used to calculate how many tags/risk badges fit in visible rows. */}
        <div className={s['tag-measure-container']}>
          {hazardRiskSummaries.map((summary, idx) => (
            <div
              key={`${summary.label}-${idx}`}
              ref={el => (hazardSummaryMeasureRefs.current[idx] = el)}
              className="d-inline-flex align-items-center ml-2"
            >
              <span className={s['risk-dot']} style={{ backgroundColor: summary.color }} />
              <span className="ml-1 font-size-sm text-nowrap">
                {showHazardRiskCount ? `${summary.count} ` : ''}
                {summary.label}
              </span>
            </div>
          ))}

          {(tags || []).map((tag, idx) => (
            <div key={tag.name} ref={el => (tagBadgeRefs.current[idx] = el)} className="d-inline-block">
              <Badge color="#902bf5" status={tag.name} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default SafetyCaseCard
